import React, { useEffect, useCallback, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";
import MaintenanceRecordsList from "../../components/maintenance/MaintenanceRecordsList";
import { STARTING_PAGE } from "../../constants/constants";
import {
  fetchMaintenanceRecords,
  fetchMaintenanceRecordsSuccess,
  fetchMaintenanceRecordsFailure,
  fetchMachines,
  fetchMachinesSuccess,
  fetchMachinesFailure,
  fetchServiceProviders,
  fetchServiceProvidersSuccess,
  fetchServiceProvidersFailure,
  startMaintenanceRecord,
  startMaintenanceRecordSuccess,
  startMaintenanceRecordFailure,
  cancelMaintenanceRecord,
  cancelMaintenanceRecordSuccess,
  cancelMaintenanceRecordFailure,
} from "../../store/maintenance/actions";

const MaintenanceRecordsListContainer = ({
  records,
  machines,
  serviceProviders,
  fetchMaintenanceRecords,
  fetchMachines,
  fetchServiceProviders,
  startMaintenanceRecord,
  cancelMaintenanceRecord,
}) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(STARTING_PAGE);
  const [filters, setFilters] = useState({
    machine: null,
    status: null,
    maintenanceType: null,
    serviceProvider: null,
    search: null,
  });

  useEffect(() => {
    fetchMaintenanceRecords(STARTING_PAGE);
    fetchMachines();
    fetchServiceProviders();
  }, [fetchMaintenanceRecords, fetchMachines, fetchServiceProviders]);

  const handleFilter = useCallback(
    (newFilters) => {
      setFilters(newFilters);
      setCurrentPage(STARTING_PAGE);
      fetchMaintenanceRecords(
        STARTING_PAGE,
        newFilters.machine || null,
        newFilters.status || null,
        newFilters.maintenanceType || null,
        newFilters.serviceProvider || null,
        newFilters.search || null
      );
    },
    [fetchMaintenanceRecords]
  );

  const handlePageRequest = useCallback(
    (data) => {
      const page = data.selected + 1;
      if (page !== currentPage) {
        setCurrentPage(page);
        fetchMaintenanceRecords(
          page,
          filters.machine || null,
          filters.status || null,
          filters.maintenanceType || null,
          filters.serviceProvider || null,
          filters.search || null
        );
      }
    },
    [currentPage, filters, fetchMaintenanceRecords]
  );

  const handleStart = useCallback(
    (id) => {
      startMaintenanceRecord(id, () => {
        fetchMaintenanceRecords(currentPage);
      });
    },
    [startMaintenanceRecord, fetchMaintenanceRecords, currentPage]
  );

  const handleComplete = useCallback(
    (id) => {
      navigate(`/maintenance/records/${id}`);
    },
    [navigate]
  );

  const handleCancel = useCallback(
    (id) => {
      if (window.confirm("Are you sure you want to cancel this maintenance?")) {
        cancelMaintenanceRecord(id, () => {
          fetchMaintenanceRecords(currentPage);
        });
      }
    },
    [cancelMaintenanceRecord, fetchMaintenanceRecords, currentPage]
  );

  return (
    <>
      <MaintenanceRecordsList
        records={records.list}
        machines={machines.list}
        serviceProviders={serviceProviders.list}
        loading={records.loading}
        error={records.error}
        onFilter={handleFilter}
        onStart={handleStart}
        onComplete={handleComplete}
        onCancel={handleCancel}
      />
      {records.pages > 1 && (
        <ReactPaginate
          pageCount={records.pages}
          pageRangeDisplayed={2}
          marginPagesDisplayed={1}
          previousLabel="<"
          nextLabel=">"
          onPageChange={handlePageRequest}
          initialPage={currentPage - 1}
          disableInitialCallback={true}
          containerClassName="pagination"
          pageClassName="page-item"
          pageLinkClassName="page-link"
          activeClassName="page-item active"
          previousClassName="paginate_button previous"
          nextClassName="paginate_button next"
          disabledClassName="page-item disabled"
          forcePage={currentPage - 1}
        />
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  records: state.maintenance.records,
  machines: state.maintenance.machines,
  serviceProviders: state.maintenance.serviceProviders,
});

const mapDispatchToProps = (dispatch) => ({
  fetchMaintenanceRecords: (page, machine, status, maintenanceType, serviceProvider, search) => {
    dispatch(fetchMaintenanceRecords(page, machine, status, maintenanceType, serviceProvider, search)).payload.then(
      (response) => {
        dispatch(fetchMaintenanceRecordsSuccess(response.data));
      },
      (error) => {
        dispatch(fetchMaintenanceRecordsFailure(error.response?.data || error));
      }
    );
  },
  fetchMachines: () => {
    dispatch(fetchMachines(1, null, null, null, null, 9999)).payload.then(
      (response) => {
        dispatch(fetchMachinesSuccess(response.data));
      },
      (error) => {
        dispatch(fetchMachinesFailure(error.response?.data || error));
      }
    );
  },
  fetchServiceProviders: () => {
    dispatch(fetchServiceProviders(1, null, null, 9999)).payload.then(
      (response) => {
        dispatch(fetchServiceProvidersSuccess(response.data));
      },
      (error) => {
        dispatch(fetchServiceProvidersFailure(error.response?.data || error));
      }
    );
  },
  startMaintenanceRecord: (id, callback) => {
    dispatch(startMaintenanceRecord(id)).payload.then(
      (response) => {
        dispatch(startMaintenanceRecordSuccess(response.data));
        if (callback) callback();
      },
      (error) => {
        dispatch(startMaintenanceRecordFailure(error.response?.data || error));
      }
    );
  },
  cancelMaintenanceRecord: (id, callback) => {
    dispatch(cancelMaintenanceRecord(id)).payload.then(
      (response) => {
        dispatch(cancelMaintenanceRecordSuccess(response.data));
        if (callback) callback();
      },
      (error) => {
        dispatch(cancelMaintenanceRecordFailure(error.response?.data || error));
      }
    );
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(MaintenanceRecordsListContainer);
